import type { Country } from 'react-phone-number-input'

/**
 * Maps the app's BCP-47 locale (as returned by useLocale) to the country whose
 * dialing code we default to. Intentionally coarse: one canonical country per
 * supported language.
 */
export const LOCALE_TO_COUNTRY: Partial<Record<string, Country>> = {
  'pt-BR': 'BR',
  'en-US': 'US',
  'es-ES': 'ES',
  'fr-FR': 'FR',
  'de-DE': 'DE',
  'it-IT': 'IT',
}

/**
 * Country Cloudflare resolved for this visitor, read from its own `/cdn-cgi/trace`
 * (the `loc` line). Same origin, no key and no route of ours: Cloudflare answers it
 * at the edge before the request reaches the app. Returns '' off Cloudflare (local
 * dev) or when the call fails.
 */
export async function getEdgeCountry(): Promise<string> {
  try {
    const response = await fetch('/cdn-cgi/trace', { cache: 'no-store' })
    if (!response.ok) return ''

    return /^loc=([A-Z]{2})$/m.exec(await response.text())?.[1] ?? ''
  } catch {
    return ''
  }
}

/**
 * Region of the first region-bearing tag in navigator.languages, uppercased.
 * Returns '' when no tag carries a region (e.g. ['pt']) or Intl is unavailable.
 */
export function getBrowserRegion(): string {
  try {
    for (const lang of navigator.languages) {
      const region = new Intl.Locale(lang).region
      if (region) return region.toUpperCase()
    }
  } catch {
    // Intl.Locale not supported
  }
  return ''
}

/**
 * Resolves the default phone country, preferring explicit signals over inferred
 * ones: an explicit override first, then the language the user chose in the app,
 * then the browser region as a last-resort tiebreaker, then 'US'.
 *
 * The chosen app locale must outrank the browser region: navigator.languages
 * commonly lists en-US ahead of a region-less 'pt', which would otherwise force
 * a +1 default on a user who explicitly picked Portuguese.
 */
export function resolveDefaultCountry(
  locale: string,
  override?: string,
): Country {
  if (override) return override as Country

  const localeCountry = LOCALE_TO_COUNTRY[locale]
  if (localeCountry) return localeCountry

  const browserRegion = getBrowserRegion()
  if (browserRegion) return browserRegion as Country

  return 'US'
}
